<script>
  import { onMount, tick } from 'svelte';
  import { get, derived } from "svelte/store";
  import MenuBar from "../../../components/menuBar/MenuBar.svelte";
  import MenuBarItem from "../../../components/menuBar/MenuBarItem.svelte";
  import GeneralSelect from "../../../components/select/GeneralSelect.svelte";
  import GeneralButton from "../../../components/button/GeneralButton.svelte";
  import DisplayColumnsSettings from "../../../components/ganttchart/DisplayColumnsSettings.svelte";
  import GeneralPanelButton from "../../../components/button/GeneralPanelButton.svelte";
  import { translate } from "../../../i18n/i18next.js";
  import MenuBarMenuDialog from "../../../components/menuBar/menuDialog/MenuBarMenuDialog.svelte";
  import MenuBarMenuDialogButton from "../../../components/menuBar/menuDialog/MenuBarMenuDialogButton.svelte";
  import ResGanttDisplaySettingsMenuDialog from "./ResGanttDisplaySettingsMenuDialog.svelte";
  import ResGanttFilterMenuDialog from "./ResGanttFilterMenuDialog.svelte";
  import ResGanttAdminMenuDialog from "./ResGanttAdminMenuDialog.svelte";
  import SearchBox from "../../../components/ganttchart/SearchBox.svelte";
  import SimpleResGanttFilterMenuDialog from "../SimpleResGantt/SimpleResGanttFilterMenuDialog.svelte";
  import GanttHelper from '../../../components/ganttchart/GanttHelper';
  import { useQuery } from "@sveltestack/svelte-query";
  import { satisfies } from "compare-versions";
  import ResGanttMiscSettingsMenuDialog from "./ResGanttMiscSettingsMenuDialog.svelte";

  export let GanttStore

  const {
    CurrentDisplayedEndDate,
    CurrentStyle,
    DisplayedStartDate,
    GanttChartType,
    StyleObjects,
    ValidEndDate,
    ValidStartDate,
    CurrentDisplayedEndDateInclDaysOff,
    GetAdjustedDateInclDaysOff,
    SkipDaysOffForwards,
    SkipDaysOffBackwards,
    ProjectId
  } = GanttStore

  let columnSettingsDialog = null
  let isColumnSettingsDialogShown = false

  async function openColumnSettings() {
    isColumnSettingsDialogShown = true
    await tick()
    columnSettingsDialog.showModal()
  }

  function closeColumnSettings() {
    columnSettingsDialog.close()
    isColumnSettingsDialogShown = false
  }

  let searchDialogVisible = false;
  const ToggleSearchDialog = (e) => {
    if (e.ctrlKey && e.key === 'f') {
      e.preventDefault();
      searchDialogVisible = !searchDialogVisible;
    }
  }
  const DisableSearchDialog = (e) => {
    e.preventDefault();
    searchDialogVisible = false;
  }

  let dialogCurrentlyDisplayed = null
  async function onClickSettingsDialogOpen() {
    dialogCurrentlyDisplayed = "menuSelection"
  }
  function onClickSettingsDialogClose() {
    dialogCurrentlyDisplayed = null
  }

  const MoveButton_Home = () => {
    GanttStore.CurrentDisplayedStartDate.update((_) => new Date($SkipDaysOffForwards($DisplayedStartDate)));
  }

  const MoveButton_PrevPage = () => {
    const oldDate = get(GanttStore.CurrentDisplayedStartDate)
    const newDate = new Date(Math.max($ValidStartDate.getTime(), oldDate.getTime() - ($CurrentDisplayedEndDateInclDaysOff.getTime() - oldDate.getTime())))
    GanttStore.CurrentDisplayedStartDate.set($SkipDaysOffBackwards(newDate));
  }

  const MoveButton_Prev = () => {
    const oldDate = get(GanttStore.CurrentDisplayedStartDate)
    const newDate = new Date(Math.max($ValidStartDate.getTime(), oldDate.getTime() - GanttHelper.GHDAY * 1000))
    GanttStore.CurrentDisplayedStartDate.set($SkipDaysOffBackwards(newDate));
  }

  const MoveButton_Today = () => {
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    if (today > $ValidEndDate || today < $ValidStartDate) return;
    GanttStore.CurrentDisplayedStartDate.set($SkipDaysOffForwards(today))
  }

  const MoveButton_Next = () => {
    const oldDate = get(GanttStore.CurrentDisplayedStartDate)
    const span = get(GanttStore.DisplayedSpanInSeconds);
    const newDate = new Date(Math.min($ValidEndDate.getTime() - span * 1000, oldDate.getTime() + GanttHelper.GHDAY * 1000))
    if (newDate > oldDate)
      GanttStore.CurrentDisplayedStartDate.set($SkipDaysOffForwards(newDate))
  }

  const MoveButton_NextPage = () => {
    const span = get(GanttStore.DisplayedSpanInSeconds);
    const newDate = new Date(Math.min($ValidEndDate.getTime() - span * 1000, $CurrentDisplayedEndDateInclDaysOff.getTime()))
    GanttStore.CurrentDisplayedStartDate.set($SkipDaysOffForwards(newDate))
  }

  onMount(() => {
    // Ctrl+F event
    document.addEventListener('keydown', ToggleSearchDialog);
  })

  const dataMetadataQuery = useQuery(`/api/projects/${$ProjectId}/views/ResGantt/meta`, {retry: 1})
  const showSearchBox = derived(dataMetadataQuery, metadataQuery => {
    if (metadataQuery.isLoading)  return false;
    const pluginVersion = metadataQuery.data?.pluginVersion
    return satisfies(pluginVersion, ">=1.8.0");
  })
</script>

<MenuBar onClickDetailedMenuOpen={onClickSettingsDialogOpen}>
  <svelte:fragment slot="settings">
    <MenuBarItem>
      <GeneralSelect
        selectId="gantt_upperpane_styleselect"
        value={$CurrentStyle.ObjectID}
        options={$StyleObjects.map((style)=>({text: style.StyleName, value: style.ObjectID}))}
        onChangeEvent={e => CurrentStyle.set($StyleObjects.find((o) => o.ObjectID == e.target.value))}
      />
    </MenuBarItem>
    {#if $GanttChartType === GanttStore.GanttChartTypes.SimpleResGantt || $GanttChartType === GanttStore.GanttChartTypes.CustomizedResGantt}
      <MenuBarItem>
        <GeneralButton
          buttonText={translate("frontend:ganttChart.columnSettings.label")}
          buttonType="secondary"
          buttonFunction={openColumnSettings}
          buttonExtraStyle="white-space: nowrap"
        />
      </MenuBarItem>
    {/if}
    <MenuBarItem>
      {#if isColumnSettingsDialogShown}
        <DisplayColumnsSettings
          GanttStore={GanttStore}
          bind:dialog={columnSettingsDialog}
          ganttChartType={$GanttChartType}
          on:close={closeColumnSettings}
        />
      {/if}
    </MenuBarItem>
    {#if $showSearchBox}
      <MenuBarItem>
        <SearchBox
          GanttStore={GanttStore}
          CloseCallback={DisableSearchDialog}
          bind:Toggled={searchDialogVisible}
        />
      </MenuBarItem>
    {/if}
    <MenuBarItem>
      <div class="void_180px"></div>
    </MenuBarItem>
  </svelte:fragment>
  <GeneralPanelButton
    slot="pagination"
    items={[
      {
        buttonText: translate("generic.startShort"),
        buttonFunction: MoveButton_Home,
      },
      {
        buttonImage: '/assets/Pager_FL.svg',
        buttonFunction: MoveButton_PrevPage,
      },
      {
        buttonImage: '/assets/Pager_L.svg',
        buttonFunction: MoveButton_Prev,
      },
      {
        buttonText: translate("generic.today"),
        buttonFunction: MoveButton_Today,
      },
      {
        buttonImage: '/assets/Pager_R.svg',
        buttonFunction: MoveButton_Next,
      },
      {
        buttonImage: '/assets/Pager_FR.svg',
        buttonFunction: MoveButton_NextPage,
      },
    ]}
  />
</MenuBar>
{#if dialogCurrentlyDisplayed === "menuSelection"}
  <MenuBarMenuDialog onClose={onClickSettingsDialogClose}>
    <MenuBarMenuDialogButton onClick={() => dialogCurrentlyDisplayed = "displaySettings"}>
      {translate("frontend:ganttChart.settings.sections.displaySettings.title")}
    </MenuBarMenuDialogButton>
    <MenuBarMenuDialogButton onClick={() => dialogCurrentlyDisplayed = "filter"}>
      {translate("generic.filterShort")}
    </MenuBarMenuDialogButton>
    <MenuBarMenuDialogButton onClick={() => dialogCurrentlyDisplayed = "misc"}>
      {translate("frontend:ganttChart.settings.miscSettings.label")}
    </MenuBarMenuDialogButton>
    <MenuBarMenuDialogButton onClick={() => dialogCurrentlyDisplayed = "admin"}>
      {translate("frontend:generic.projectAdminSettings.title")}
    </MenuBarMenuDialogButton>
  </MenuBarMenuDialog>
{/if}
{#if dialogCurrentlyDisplayed === "displaySettings"}
  <ResGanttDisplaySettingsMenuDialog
    GanttStore={GanttStore}
    onClose={() => dialogCurrentlyDisplayed = null}
  />
{/if}
{#if dialogCurrentlyDisplayed === "filter"}
  {#if $GanttChartType === GanttStore.GanttChartTypes.SimpleResGantt}
    <SimpleResGanttFilterMenuDialog
      GanttStore={GanttStore}
      onClose={() => dialogCurrentlyDisplayed = null}
    />
  {:else}
    <ResGanttFilterMenuDialog
      GanttStore={GanttStore}
      onClose={() => dialogCurrentlyDisplayed = null}
    />
  {/if}
{/if}
{#if dialogCurrentlyDisplayed === "misc"}
  <ResGanttMiscSettingsMenuDialog
    GanttStore={GanttStore}
    onClose={() => dialogCurrentlyDisplayed = null}
  />
{/if}
{#if dialogCurrentlyDisplayed === "admin"}
  <ResGanttAdminMenuDialog
    GanttStore={GanttStore}
    onClose={() => dialogCurrentlyDisplayed = null}
  />
{/if}

<style>
  .void_180px {
    padding-right: 180px;
  }
</style>
